import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import { PERMISSIONS, User } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { DetailsTab } from '@repo/components';
import Typography from '@mui/material/Typography';
import { Spinner } from '@repo/components';
import { RequirementChip } from '@repo/components';
import { Badge } from '@repo/components';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { LanguageCode } from '@repo/shared/domain';
import { constructQuery } from '@repo/shared/helpers';
import { getPartnerCompanyEvent, getPartnerCompanyEventForm } from 'src/api/partner';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/details')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		lang: (search.lang as LanguageCode) || 'en',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions);
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();
	const { partnerId, eventId } = Route.useParams();
	const { lang } = Route.useSearch();
	useBreadcrumbLevel(3, 'Details');
	// const searchParams = useSearchParams();

	const {
		data: event,
		isLoading: eventLoading,
		error: _getPartnerCompanyEventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
	});

	const {
		data: eventForm,
		isLoading: eventFormLoading,
		error: _getPartnerCompanyEventFormError,
	} = useQuery({
		queryKey: ['partner-company-event-form', partnerId],
		queryFn: () => getPartnerCompanyEventForm(event?.type?.Form?.slug || '', constructQuery({ lang })),
	});

	if (userLoading || eventLoading || eventFormLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	const users = event?.users?.filter((user: User) => user.status === 'active');

	const printHeader = (
		<div className="flex gap-2">
			<div className="w-[300px] flex items-center">
				<img style={{ objectFit: 'contain', maxHeight: 70, width: '100%' }} src="/api/public/logo" />
			</div>
			<div className="flex-grow flex flex-row flex-wrap p-2">
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Event Name
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.name || '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Type
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.type ? (
							<RequirementChip object={{ status: event.status, type: { name: event.type?.name || '' } }} />
						) : (
							'-'
						)}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Description
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.description || '-'}
					</Typography>
				</div>
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Start Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.startTime ? new Date(event?.startTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Finish Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.finishTime ? new Date(event?.finishTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Zones
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.zones?.length ? event?.zones?.map((r) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-[40%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Owners
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.admins?.length ? event?.admins?.map((r) => <Badge key={r.email}>{r.email}</Badge>) : '-'}
					</div>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Companies
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.groups?.length ? event?.groups?.map((r) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-full">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Users
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{users?.length
							? users?.map((r) => (
									<Badge key={r.email}>
										{r.firstName} {r.lastName}
									</Badge>
								))
							: '-'}
					</div>
				</div>
			</div>
		</div>
	);

	const handleDetailsUpdate = async (eventData: Record<string, any>) => {
		await put(`/api/partner-portal/groups/${partnerId}/events/${eventId}/details`, { ...eventData })
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Changes saved',
				});
			})
			.catch(() => {
				EventEmitter.emit('toast', { type: 'error', message: 'Error' });
			});
		queryClient.invalidateQueries({ queryKey: ['partner-company-events', partnerId] });
	};

	const handleLanguageChange = (language: LanguageCode) => {
		updateQueryParams({ language });
	};

	const availableLanguages = eventForm?.availableLanguages ?? [];
	const defaultLanguage = eventForm?.language ?? 'en';
	const languages = [...new Set([...availableLanguages, defaultLanguage])] as LanguageCode[];
	const detailLanguage = languages.find((language) => language === lang) || 'en';

	return (
		<DetailsTab
			data={event?.eventData || []}
			printHeader={printHeader}
			form={eventForm}
			onLanguageChange={handleLanguageChange}
			language={detailLanguage}
			availableLanguages={languages}
			onSave={handleDetailsUpdate}
			readOnly={!verifyPermission(PERMISSIONS.partnerCompanyEventsEdit)}
		/>
	);
}
